import { OriginalWattwatchersMeter, SwitchData, WattwatchersMeter } from 'clipsal-cortex-types/src/api/api-ww-meter';
import { PaginatedResponse } from 'clipsal-cortex-types/src/common/types';
import { WATTWATCHERS_MANUFACTURER_ID } from 'clipsal-cortex-utils/src/constants/common-constants';
import { getStringifiedQueryParams } from 'clipsal-cortex-utils/src/formatting/query-params';

import { SiteData } from '../../../../api/api-site';
import { baseApi } from '../../../../app/baseApi';
import { ChannelCategoryData, LogData, LongEnergyData, ShortEnergyData, SiteMeterData } from './meter-config-types';

type CommonParams = { timezone: string };
type LongEnergyQueryParams = { reading_date: string } & CommonParams;
export type UpdateMeterDetailsBody = {
  id: string;
  channels: Omit<OriginalWattwatchersMeter['channels'][number], 'categoryLabel' | 'pending'>[];
  switches?: Omit<SwitchData, 'categoryLabel' | 'pending'>[];
};
const POLLING_INTERVAL_TIMER_MS = 5000;

export const meterConfigApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchSites: builder.query<PaginatedResponse<SiteData>, string>({
      query: (search) => `/fleet/sites?search_term=${search}&imit=5&offset=0`,
      providesTags: (_, __, search) => [{ type: 'Sites', search }],
    }),
    searchMeters: builder.query<SiteMeterData[], string>({
      query: (deviceId) => `/v1/wattwatchers/meters/search?ww_device_id=${deviceId}`,
      providesTags: (_, __, deviceId) => [{ type: 'MeterSearch', deviceId }],
    }),
    getFirstEnergyTimestamp: builder.query<{ timestamp: number }, { deviceId: string; params: CommonParams }>({
      query: ({ deviceId, params }) => {
        const stringifiedParams = getStringifiedQueryParams(params);
        return `/v1/wattwatchers/devices/${deviceId}/first?${stringifiedParams}`;
      },
      providesTags: (_, __, params) => [{ type: 'FirstEnergyTimestamp', ...params }],
    }),
    getMeterDetails: builder.query<OriginalWattwatchersMeter, string>({
      query: (deviceId) => `/v1/wattwatchers/devices/${deviceId}`,
      providesTags: (_, __, deviceId) => [{ type: 'OriginalWattWatchersMeter', deviceId }],
    }),
    getSiteMeterDetails: builder.query<WattwatchersMeter, number | null>({
      query: (siteDeviceId) => `/fleet/wattwatchers/installed_meters/${siteDeviceId}`,
      providesTags: (_, __, siteDeviceId) => [{ type: 'WattWatchersMeter', siteDeviceId }],
    }),
    getShortEnergy: builder.query<ShortEnergyData[], string>({
      query: (deviceId) => `/v1/wattwatchers/devices/${deviceId}/short_energy`,
      providesTags: (_, __, deviceId) => [{ type: 'ShortEnergy', deviceId }],
    }),
    getLongEnergy: builder.query<LongEnergyData[], { deviceId: string; params: LongEnergyQueryParams }>({
      query: ({ deviceId, params }) => {
        const stringifiedParams = getStringifiedQueryParams(params);
        return `/v1/wattwatchers/devices/${deviceId}/long_energy?${stringifiedParams}`;
      },
      providesTags: (_, __, params) => [{ type: 'LongEnergy', ...params }],
    }),
    getChannelCategories: builder.query<ChannelCategoryData[], void>({
      query: () => `/v1/wattwatchers/channel_categories`,
      providesTags: ['ChannelCategories'],
    }),
    getLogs: builder.query<PaginatedResponse<LogData>, string>({
      query: (deviceId) => `/v1/wattwatchers/devices/${deviceId}/logs?ordering=-timestamp`,
      providesTags: (_, __, deviceId) => [{ type: 'Logs', deviceId }],
    }),
    updateMeterDetails: builder.mutation<OriginalWattwatchersMeter, UpdateMeterDetailsBody>({
      query: (body) => {
        return {
          url: `/v1/wattwatchers/devices/${body.id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, __, { id: deviceId }) => [
        { type: 'OriginalWattWatchersMeter', deviceId },
        { type: 'Logs', deviceId },
      ],
    }),
    updateSiteMeterDetails: builder.mutation<WattwatchersMeter, WattwatchersMeter>({
      query: (body) => {
        return {
          url: `/fleet/wattwatchers/installed_meters/${body.installed_device_id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, __, { installed_device_id: siteDeviceId, ww_device_id: deviceId }) => [
        { type: 'WattWatchersMeter', siteDeviceId },
        { type: 'Logs', deviceId },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useSearchSitesQuery,
  useSearchMetersQuery,
  useGetFirstEnergyTimestampQuery,
  useGetMeterDetailsQuery,
  useGetSiteMeterDetailsQuery,
  useGetLongEnergyQuery,
  useGetShortEnergyQuery,
  useGetChannelCategoriesQuery,
  useGetLogsQuery,
  useUpdateMeterDetailsMutation,
  useUpdateSiteMeterDetailsMutation,
} = meterConfigApi;

export const useSearchSites = (search: string, skip?: boolean) => {
  const queryResult = useSearchSitesQuery(search, { skip: skip || !search });
  const { data } = queryResult;

  const searchResults: SiteMeterData[] = [];
  // flatten the results
  if (data?.data)
    data.data.forEach((site) => {
      let hasWattwatchersMeter = false;

      // Check if the site has a wattwatchers meter
      // If it does, add them to the search results
      site.meters!.forEach((meter) => {
        if (meter.meta_data.manufacturer_id === WATTWATCHERS_MANUFACTURER_ID) {
          hasWattwatchersMeter = true;
          searchResults.push({
            clipsal_solar_id: site.clipsal_solar_id,
            site_name: site.site_name,
            site_identifier: meter.site_identifier,
          });
        }
      });

      // If no wattwatchers meter in the site display unavailable
      // Might be useful for users to locate issues
      if (!hasWattwatchersMeter) {
        searchResults.push({
          clipsal_solar_id: site.clipsal_solar_id,
          site_name: site.site_name,
          site_identifier: null,
        });
      }
    });
  return { ...queryResult, searchResults };
};

export const useSearchMeters = (search: string, skip?: boolean) => {
  const queryResult = useSearchMetersQuery(search, { skip: skip || !search });
  return { ...queryResult, data: queryResult.data || [] };
};

export const useFirstEnergyTimestamp = (deviceId: string, params: CommonParams) => {
  const queryResult = useGetFirstEnergyTimestampQuery({ deviceId, params });
  return { ...queryResult, data: queryResult.data || { timestamp: 0 } };
};

export const useMeter = (deviceId: string) => {
  const queryResult = useGetMeterDetailsQuery(deviceId, {
    skip: !deviceId,
    pollingInterval: deviceId ? POLLING_INTERVAL_TIMER_MS : undefined,
  });
  return queryResult;
};

export const useSiteMeter = (siteDeviceId: number | null) => {
  const queryResult = useGetSiteMeterDetailsQuery(siteDeviceId, { skip: !siteDeviceId });
  return queryResult;
};

export const useShortEnergy = (deviceId: string, skip?: boolean) => {
  const skipPolling = skip || !deviceId;
  const queryResult = useGetShortEnergyQuery(deviceId, {
    skip: skipPolling,
    pollingInterval: skipPolling ? undefined : POLLING_INTERVAL_TIMER_MS,
  });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useLongEnergy = (deviceId: string, params: LongEnergyQueryParams, skip?: boolean) => {
  const queryResult = useGetLongEnergyQuery({ deviceId, params }, { skip: skip || !deviceId });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useChannelCategories = () => {
  const queryResult = useGetChannelCategoriesQuery();
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useLogs = (deviceId: string) => {
  const queryResult = useGetLogsQuery(deviceId);
  return { ...queryResult, data: queryResult.data?.data ?? [] };
};
